<template>
  <div
    ref="elRef"
    class="wl-checkbox group relative flex cursor-pointer items-center rounded-2xl transition duration-150"
    :class="classes"
    @click.stop="onToggle"
  >
    <div
      ref="checkboxWrapperRef"
      class="wl-checkbox-wrapper border-emphasis bg-default group-hover:border-accent-emphasis group-has-[:focus-visible]:ring-accent relative size-4 flex-shrink-0 rounded border transition duration-150 group-has-[:focus-visible]:ring-1 hover:border"
    >
      <input
        :id="inputId"
        ref="inputRef"
        class="pointer-events-none absolute inset-0 opacity-0"
        :name="inputId"
        :disabled="isDisabled"
        :checked="modelValue"
        :tabindex="0"
        type="checkbox"
        @click.stop
        @change.stop="onToggle"
      />
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        class="absolute inset-[-1px]"
      >
        <path
          d="M5 9.063 8.6 13 15 6"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <label
      v-if="label || subLabel"
      :for="inputId"
      class="pointer ml-3 cursor-pointer truncate select-none"
      @click.stop
    >
      <span
        v-if="label"
        ref="labelRef"
        class="text-emphasis block truncate text-sm leading-5 font-medium"
      >
        {{ label }}
      </span>
      <span
        v-if="subLabel"
        ref="subLabelRef"
        class="text-muted block truncate text-sm leading-5 font-normal"
      >
        {{ subLabel }}
      </span>
    </label>
    <div
      v-if="$slots.suffix"
      ref="suffixRef"
      class="ml-auto"
      @click.stop="onToggle"
    >
      <slot name="suffix" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, onMounted } from "vue";
import { useFocusWithin } from "@vueuse/core";
import { useWebHaptics } from "../../composables/use-web-haptics";
import type { CheckboxEmits, CheckboxProps } from "./types";

const elRef = ref();
const inputRef = ref();
const emit = defineEmits<CheckboxEmits>();
const props = withDefaults(defineProps<CheckboxProps>(), {
  isBoxed: false,
  isDisabled: false,
  autoFocus: false,
});

const { inputId, modelValue, label, subLabel, isDisabled, isBoxed, autoFocus } =
  toRefs(props);

const { focused: isFocused } = useFocusWithin(elRef);
const { triggerHaptic } = useWebHaptics();

const classes = computed(() => [
  isFocused.value && isBoxed.value ? "bg-muted" : "bg-transparent",
  { "is-boxed py-3.5": isBoxed.value },
  {
    "is-disabled pointer-events-none cursor-default opacity-30":
      isDisabled.value,
  },
  { "is-checked": modelValue?.value },
  { "is-focused": !isDisabled.value && isFocused },
]);

const onToggle = (): void => {
  if (isDisabled?.value) {
    return;
  }

  triggerHaptic("soft");
  emit("update:modelValue", !modelValue?.value);
};

onMounted(() => {
  if (autoFocus.value) {
    inputRef.value.focus();
  }
});

defineExpose({ elRef, inputRef });
</script>
<style>
@reference "@theme";

.wl-checkbox {
  .wl-checkbox-wrapper {
    svg path {
      stroke-dashoffset: 16;
      stroke-dasharray: 16;
    }
  }

  &.is-checked {
    .wl-checkbox-wrapper {
      @apply bg-accent border-transparent;

      svg {
        @apply size-4 opacity-100;

        path {
          stroke-dashoffset: 0;
          @apply stroke-inverted transition-all delay-150 duration-150;
        }
      }
    }
  }

  &.has-error {
    .wl-checkbox-wrapper {
      @apply border-accent;
    }
  }
}
</style>
